import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { StaffShell } from "@/components/layout/StaffShell";
import { adminNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CenterSpinner } from "@/components/common/States";

const DSR_STATUSES = ["open", "in_progress", "resolved", "rejected"];
const INCIDENT_STATUSES = ["open", "investigating", "contained", "resolved", "reported"];
const SEVERITIES = ["low", "medium", "high", "critical"];

const toneFor = (s) =>
  ({ open: "destructive", in_progress: "secondary", investigating: "secondary", contained: "secondary", resolved: "default", reported: "default", rejected: "outline" }[s] || "secondary");

function Requests() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-dsr"], queryFn: () => api("/privacy/admin/requests") });
  const resolve = async (id, status) => {
    try {
      await api(`/privacy/admin/requests/${id}`, { method: "PATCH", json: { status } });
      qc.invalidateQueries({ queryKey: ["admin-dsr"] });
      qc.invalidateQueries({ queryKey: ["admin-audit"] });
      toast.success(`Request → ${status.replace("_", " ")}`);
    } catch (e) {
      toast.error(e.message);
    }
  };
  if (isLoading) return <CenterSpinner />;
  return (
    <Card className="overflow-hidden rounded-2xl">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Kind</TableHead>
            <TableHead>Requester</TableHead>
            <TableHead>Detail</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Set status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data || []).map((r) => (
            <TableRow key={r.request_id} data-testid={`dsr-${r.request_id}`}>
              <TableCell className="font-medium capitalize">{r.kind}</TableCell>
              <TableCell className="text-muted-foreground">{r.email}</TableCell>
              <TableCell className="max-w-xs truncate text-muted-foreground">{r.detail || "—"}</TableCell>
              <TableCell><Badge variant={toneFor(r.status)} className="rounded-full font-normal capitalize">{r.status.replace("_", " ")}</Badge></TableCell>
              <TableCell>
                <Select value={r.status} onValueChange={(v) => resolve(r.request_id, v)}>
                  <SelectTrigger className="h-8 w-36 rounded-full" data-testid={`dsr-status-${r.request_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DSR_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
          {!data?.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No data-subject requests yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function AuditLog() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-audit"], queryFn: () => api("/privacy/admin/audit") });
  if (isLoading) return <CenterSpinner />;
  return (
    <Card className="overflow-hidden rounded-2xl">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>Actor</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Target</TableHead>
            <TableHead>Meta</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data || []).map((a) => (
            <TableRow key={a.audit_id} data-testid={`audit-${a.audit_id}`}>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{new Date(a.at).toLocaleString()}</TableCell>
              <TableCell className="text-muted-foreground">{a.actor_email || a.actor_id}</TableCell>
              <TableCell><Badge variant="secondary" className="rounded-full font-normal">{a.action}</Badge></TableCell>
              <TableCell className="text-muted-foreground">{a.target || "—"}</TableCell>
              <TableCell className="max-w-xs truncate text-xs text-muted-foreground">{a.meta ? JSON.stringify(a.meta) : "—"}</TableCell>
            </TableRow>
          ))}
          {!data?.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No audit records yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function Incidents() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-incidents"], queryFn: () => api("/privacy/admin/incidents") });
  const [form, setForm] = useState({ title: "", description: "", severity: "medium", affected_data_subjects: 0 });
  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      await api("/privacy/admin/incidents", { method: "POST", json: { ...form, affected_data_subjects: Number(form.affected_data_subjects) || 0 } });
      setForm({ title: "", description: "", severity: "medium", affected_data_subjects: 0 });
      qc.invalidateQueries({ queryKey: ["admin-incidents"] });
      qc.invalidateQueries({ queryKey: ["admin-audit"] });
      toast.success("Incident logged");
    } catch (e) {
      toast.error(e.message);
    }
  };
  const patch = async (id, body) => {
    try {
      await api(`/privacy/admin/incidents/${id}`, { method: "PATCH", json: body });
      qc.invalidateQueries({ queryKey: ["admin-incidents"] });
      qc.invalidateQueries({ queryKey: ["admin-audit"] });
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl p-5">
        <h3 className="font-display text-lg">Log a security incident</h3>
        <p className="mt-1 text-sm text-muted-foreground">POPIA §22 requires notifying the Regulator and affected data subjects where a breach is likely to cause harm.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-full" data-testid="incident-title" />
          <div className="flex gap-3">
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger className="rounded-full" data-testid="incident-severity"><SelectValue /></SelectTrigger>
              <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" min={0} placeholder="Affected" value={form.affected_data_subjects} onChange={(e) => setForm({ ...form, affected_data_subjects: e.target.value })} className="w-28 rounded-full" data-testid="incident-affected" />
          </div>
          <Textarea placeholder="What happened, systems involved, data affected…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sm:col-span-2" data-testid="incident-description" />
        </div>
        <Button className="mt-3 rounded-full" onClick={create} data-testid="incident-create">Log incident</Button>
      </Card>
      {isLoading ? <CenterSpinner /> : (data || []).map((i) => (
        <Card key={i.incident_id} className="rounded-2xl p-5" data-testid={`incident-${i.incident_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <AlertTriangle className={`h-4 w-4 ${i.severity === "critical" || i.severity === "high" ? "text-destructive" : "text-muted-foreground"}`} />
            <span className="font-display text-lg">{i.title}</span>
            <Badge variant="outline" className="rounded-full capitalize">{i.severity}</Badge>
            <Badge variant={toneFor(i.status)} className="rounded-full font-normal capitalize">{i.status}</Badge>
            {i.regulator_notified && <Badge className="rounded-full">Regulator notified</Badge>}
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{i.description || "—"}</p>
          <p className="mt-1 text-xs text-muted-foreground">Affected data subjects: {i.affected_data_subjects} · Discovered {new Date(i.discovered_at).toLocaleString()}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Select value={i.status} onValueChange={(v) => patch(i.incident_id, { status: v })}>
              <SelectTrigger className="h-8 w-40 rounded-full" data-testid={`incident-status-${i.incident_id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{INCIDENT_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
            {!i.regulator_notified && <Button variant="outline" size="sm" className="rounded-full" onClick={() => patch(i.incident_id, { regulator_notified: true })} data-testid={`incident-notify-${i.incident_id}`}>Mark Regulator notified</Button>}
          </div>
        </Card>
      ))}
    </div>
  );
}

function Retention() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-retention"], queryFn: () => api("/privacy/admin/retention") });
  const [running, setRunning] = useState(false);
  const run = async () => {
    setRunning(true);
    try {
      const res = await api("/privacy/admin/retention/run", { method: "POST" });
      qc.invalidateQueries({ queryKey: ["admin-retention"] });
      qc.invalidateQueries({ queryKey: ["admin-audit"] });
      toast.success(`Sweep complete — ${res.inactive_accounts_anonymised} account(s) anonymised`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  };
  if (isLoading) return <CenterSpinner />;
  const cfg = data?.config || {};
  const el = data?.eligible_now || {};
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl p-5">
        <h3 className="font-display text-lg">Retention schedule</h3>
        <p className="mt-1 text-sm text-muted-foreground">Sensible SA defaults — <span className="font-medium">BUSINESS_DECISION_REQUIRED</span>. Adjust via backend env vars.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[
            ["Inactive account anonymisation", `${cfg.inactive_account_days} days`],
            ["Order / financial records", `${cfg.order_record_years} years (retained)`],
            ["Notifications", `${cfg.notification_days} days`],
            ["Resolved DSRs", `${cfg.dsr_resolved_days} days`],
            ["Consent events", `${cfg.consent_event_years} years`],
            ["Auto-sweep interval", `${cfg.sweep_interval_hours} hours`],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between rounded-xl border p-3 text-sm">
              <span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span>
            </div>
          ))}
        </div>
      </Card>
      <Card className="rounded-2xl p-5">
        <h3 className="font-display text-lg">Eligible for cleanup now</h3>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <span data-testid="eligible-notifications">Notifications: <b>{el.notifications ?? 0}</b></span>
          <span data-testid="eligible-dsr">Resolved DSRs: <b>{el.resolved_dsr ?? 0}</b></span>
          <span data-testid="eligible-consent">Consent events: <b>{el.consent_events ?? 0}</b></span>
          <span data-testid="eligible-accounts">Inactive accounts: <b>{el.inactive_accounts ?? 0}</b></span>
        </div>
        <Button className="mt-4 rounded-full" onClick={run} disabled={running} data-testid="retention-run">
          {running ? "Running…" : "Run retention sweep now"}
        </Button>
      </Card>
    </div>
  );
}

export default function AdminCompliance() {
  return (
    <StaffShell items={adminNav} roleLabel="Admin" title="Compliance">
      <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
        <ShieldCheck className="h-4 w-4 text-secondary" /> POPIA / PAIA governance — data-subject requests, audit trail, breach register &amp; retention.
      </div>
      <Tabs defaultValue="requests">
        <TabsList className="rounded-full" data-testid="compliance-tabs">
          <TabsTrigger value="requests" data-testid="tab-requests">Requests</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents">Incidents</TabsTrigger>
          <TabsTrigger value="retention" data-testid="tab-retention">Retention</TabsTrigger>
        </TabsList>
        <TabsContent value="requests" className="mt-4"><Requests /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditLog /></TabsContent>
        <TabsContent value="incidents" className="mt-4"><Incidents /></TabsContent>
        <TabsContent value="retention" className="mt-4"><Retention /></TabsContent>
      </Tabs>
    </StaffShell>
  );
}
